import React, { useEffect, useState } from "react";
// @mui
import { Container, List, ListItem, Paper, Typography, useTheme } from "@mui/material";
import * as XLSX from "xlsx";
import { DataGrid } from "@mui/x-data-grid";

// styled components
import FeeStructureSrc from "assets/documents/Fee structure-DPS-SHARJAH-2023-24-Najmat.xlsx";
import useResponsive from "hooks/useResponsive";
import DueDates from "./dueDate";
import { red } from "@mui/material/colors";

const fetchData = async (setState, sheetIndex = 0) => {
  try {
    const response = await fetch(FeeStructureSrc);
    const blob = await response.blob();

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = event.target.result;
      const workbook = XLSX.read(data, { type: "binary" });
      const sheetName = workbook.SheetNames[sheetIndex];
      const worksheet = workbook.Sheets[sheetName];
      const excelData = XLSX.utils.sheet_to_json(worksheet);
      if (excelData) {
        const FinalData = excelData.map((dataitem, index) => ({
          id: index + 1,
          ...dataitem,
        }));
        setState(FinalData);
      }
    };
    reader.readAsBinaryString(blob);
  } catch (error) {
    console.error("Error occurred while reading the file:", error);
  }
};

const buildHeaders = (data) => {
  const keys = Object.keys(data[0]);
  const filteredKeys = keys.filter((key) => key !== "id");
  return filteredKeys.map((key) => ({
    headerName: key.toString().toLowerCase(),
    field: key,
    flex: key.includes("Covering") ? 4 : 1,
    display: "flex",
    flexWrap: "wrap",
    whiteSpace: "break-spaces",
    minWidth: key.length > 15 ? 220 : 80,
    sortable: false,
  }));
};

const CONFIG = {
  year: "2026-2027",
};

const tableStyles = {
  "& .MuiDataGrid-cellContent": {
    whiteSpace: "break-spaces",
    padding: "8px 0px",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    textTransform: "uppercase",
    fontSize: 12,
  },
};

function FeeSection({ id, white = true }) {
  const theme = useTheme();
  const [ExcelData, setExcelData] = useState([]);
  const [TableHeaders, setTableHeaders] = useState([]);
  const [ExcelData2, setExcelData2] = useState([]);
  const [TableHeaders2, setTableHeaders2] = useState([]);
  const mdDown = useResponsive("down", "md");

  useEffect(() => {
    if (!ExcelData.length) fetchData(setExcelData, 0);
    if (ExcelData.length) setTableHeaders(buildHeaders(ExcelData));
  }, [ExcelData]);

  useEffect(() => {
    if (!ExcelData2.length) fetchData(setExcelData2, 5);
    if (ExcelData2.length) setTableHeaders2(buildHeaders(ExcelData2));
  }, [ExcelData2]);

  return (
    <div style={{ background: white && theme.palette.background.paper }}>
      <Container maxWidth="xl" sx={{ py: 10 }}>
        <Typography
          variant={mdDown ? "h3" : "h1"}
          textAlign="center"
          fontWeight="700"
          fontFamily='"Merriweather", serif'
        >
          Transport fees for the academic year {CONFIG.year}
        </Typography>
        <Typography variant="h4" textAlign="center" mt={2} mx={4} px={3} sx={{ color: red[400] }}>
          For grade 6 to 12 (senior section) fee w.e.f. 23rd April 2026
        </Typography>

        {/* Table 1 - SheetNames[0] */}
        {TableHeaders.length > 0 && (
          <Paper sx={{ mt: 5, ...tableStyles }}>
            <DataGrid
              rowSelection={false}
              rows={ExcelData}
              getRowHeight={() => "auto"}
              columns={TableHeaders}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 10 } },
              }}
              disableColumnMenu
              hideFooterPagination={true}
            />
          </Paper>
        )}

        <Typography
          mt={5}
          variant={mdDown ? "h3" : "h1"}
          textAlign="center"
          fontWeight="700"
          fontFamily='"Merriweather", serif'
        >
          Transport fees for the academic year {CONFIG.year}
        </Typography>
        <Typography variant="h4" textAlign="center" mt={2} mx={4} px={3} sx={{ color: red[400] }}>
          For grade KG to 5 (primary section) fee w.e.f. 27th April 2026
        </Typography>

        {/* Table 2 - SheetNames[5] */}
        {TableHeaders2.length > 0 && (
          <Paper sx={{ mt: 6, ...tableStyles }}>
            <DataGrid
              rowSelection={false}
              rows={ExcelData2}
              getRowHeight={() => "auto"}
              columns={TableHeaders2}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 10 } },
              }}
              disableColumnMenu
              hideFooterPagination={true}
            />
          </Paper>
        )}

        <div style={{ marginTop: 16 }}>
          <Typography textTransform="uppercase" sx={{ fontWeight: "bold" }} mt={3} mb={1}>
            Payment Due Dates For New Admissions :
          </Typography>
          <Typography variant="body1">Immediately upon admission </Typography>
        </div>

        <div style={{ marginTop: 16 }}>
          <Typography textTransform="uppercase" sx={{ fontWeight: "bold" }} mt={3} mb={1}>
            Payment Due Dates For Regular Students{" "}
          </Typography>
          <div style={{ display: "flex", width: "100%" }}>
            <DueDates />
          </div>
        </div>

        <List sx={{ mt: 3 }}>
          <Typography mb={2}>NOTE</Typography>
          <ListItem>
            ⚫ Student will not be allowed to board the bus in case payment is not received before
            last date of payment (as mentioned above)
          </ListItem>
          <ListItem>
            ⚫ Vat or any other taxes if applicable on transport fee shall be charged additionally
            on above amounts.
          </ListItem>
          <ListItem>
            ⚫ Once the RFID card has been issued and the bus/seat allocation has been done, the fee
            is payable, irrespective of the usage or non-usage of the bus service.
          </ListItem>
        </List>
      </Container>
    </div>
  );
}

export default FeeSection;
